import React, { useContext, useEffect, useState } from 'react';
import { Button, Input, List, Space } from 'antd';

import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { OrganizationExtras } from '@totem/types/organization';
import {
  API_CONNECTIONS_ENDPOINT,
  ORGANIZATION_ENDPOINT,
} from '@totem/utilities/endpoints';

import './account.css';

interface ApiConnectionSummary {
  id: string;
  type: string;
  displayName: string;
  status: string;
}

const FinancialPanel = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { orgDetails, doRefresh } = useContext(OrganizationContext);

  const [fiscalYearEnd, setFiscalYearEnd] = useState<string>('');
  const [maxInactivityDays, setMaxInactivityDays] = useState<number>(0);
  const [remoteAccessConnectionId, setRemoteAccessConnectionId] =
    useState<string>('');
  const [apiConnections, setApiConnections] = useState<ApiConnectionSummary[]>(
    [],
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (orgDetails?.extras) {
      setFiscalYearEnd(orgDetails.extras.fiscalCalendarYearEnd ?? '');

      setMaxInactivityDays(
        orgDetails.extras.remoteAccessMaxInactivityDays ?? 0,
      );

      setRemoteAccessConnectionId(
        orgDetails.extras.remoteAccessConnection?.id ?? '',
      );
    }
  }, [orgDetails]);

  useEffect(() => {
    if (
      !permissions.remoteAccessConfigurationAdmin.hasPermissions(userProfile)
    ) {
      return;
    }

    fetch(API_CONNECTIONS_ENDPOINT, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: ApiConnectionSummary[]) => {
        const filtered = result.filter(
          (conn) =>
            conn.type === 'Tempered_Conductor' && conn.status !== 'Inactive',
        );
        setApiConnections(filtered);
      });
  }, [userProfile, getApiToken]);

  const handleSave = () => {
    if (isSaving) {
      return;
    }

    const selectedConnection = apiConnections.find(
      (conn) => conn.id === remoteAccessConnectionId,
    );

    const payload: OrganizationExtras = {
      fiscalCalendarYearEnd: fiscalYearEnd,
      remoteAccessMaxInactivityDays: maxInactivityDays,
      remoteAccessConnection: selectedConnection
        ? { id: selectedConnection.id, name: selectedConnection.displayName }
        : (orgDetails?.extras?.remoteAccessConnection ?? { id: '', name: '' }),
      buildingSpecific: orgDetails?.extras?.buildingSpecific ?? [],
    };

    setIsSaving(true);

    fetch(`${ORGANIZATION_ENDPOINT}/current/extras`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    })
      .then(() => {
        doRefresh();
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <>
      <List.Item>
        <List.Item.Meta
          style={{ alignItems: 'inherit' }}
          title={<div styleName="item-title">Fiscal Calendar Year End</div>}
          description="The month and day the organization's fiscal year ends (e.g., 12-31)."
        />
        <div>
          <Input
            value={fiscalYearEnd}
            onChange={(evt) => setFiscalYearEnd(evt.target.value)}
            placeholder="MM-DD"
            style={{ maxWidth: '100px' }}
          />
        </div>
      </List.Item>

      <List.Item>
        <br />
        <Space>
          <Button type="primary" loading={isSaving} onClick={handleSave}>
            Save
          </Button>
        </Space>
      </List.Item>
    </>
  );
};

export default FinancialPanel;
